import {
	Button,
	Checkbox,
	Input,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material';
import axios from 'axios';
import moment from 'moment';
import { useEffect, useState } from 'react';
import {
	DeleteButton,
	EditButton,
	ImageField,
	ReferenceField,
	Show,
	ShowButton,
	Tab,
	TabbedShowLayout,
	TextField,
	useNotify,
	usePermissions,
	useRecordContext,
	useShowController,
} from 'react-admin';
import constants from '../../constants';
import MemberProactivePlanForm from '../Inputs/MemberProactivePlanForm';
import MemberDevicesTab from './MemberDevicesTab';
import { MdOutlineEmail } from 'react-icons/md';

interface Lecture {
	id: string;
	title: string;
	expert_name: string;
	time: string;
	image: string;
	sub_title?: {
		he: string;
	};
	reason: string;
	data?: {
		service?: {
			title: string;
			sub_title?: {
				he: string;
			};
			image: string;
			time: string;
		};
	};
}

interface MediaItem {
	id: string;
	title: string;
	expert_name: string;
	time: string;
	image: string;
	sub_title?: string;
	reason: string;
	data?: {
		title: string;
		sub_title: string;
		thumbnail: string;
		time: string;
		service?: {
			title: string;
			sub_title?: string;
			image: string;
			time: string;
		};
	};
}

interface Service {
	id: string;
	title: string;
	image: string;
	description: string;
	reason: string;
	data?: any;
}

interface Expert {
	id: string;
	name: string;
	title: string;
	image: string;
	reason: string;
	data?: {
		title: string;
		name: string;
		image: string;
		description_lng?: {
			he: string;
		};
	};
}

interface PrivateMeeting {
	sessions: Array<{
		_id: string;
		title: string;
		time: string;
		service?: any;
		price?: number;
	}>;
}

// משתמש-עוגן: נועד להזמין בני משפחה שאינם רשומים (זרימת family-invite). אסור למחוק.
const FAMILY_INVITE_ANCHOR_ID = '6a2686ad595ccba84188b871';

const AnchorMemberNotice = () => {
	const record = useRecordContext();
	if (!record || String(record.id) !== FAMILY_INVITE_ANCHOR_ID) return null;
	return (
		<div
			style={{
				border: '2px solid #e65100',
				background: '#fff3e0',
				borderRadius: 8,
				padding: 16,
				margin: '8px 0 16px',
				textAlign: 'center',
			}}
		>
			<Typography variant="h6" sx={{ color: '#e65100', fontWeight: 700 }}>
				⚠️ משתמש מערכת — אין למחוק!
			</Typography>
			<Typography variant="body1" sx={{ color: '#bf360c', marginTop: 1 }}>
				זהו משתמש-עוגן שנועד להזמנת בני משפחה שאינם רשומים (זרימת family-invite). בני המשפחה
				המוזמנים מקושרים אליו דרך <strong>invited_by</strong>. מחיקה תשבור את ההזמנות הקיימות.
			</Typography>
		</div>
	);
};

const FamilyRelations = () => {
	const record = useRecordContext();
	if (!record) return null;

	const fm = record.family_member;
	const invitedSomeone = Boolean(fm && (fm.member_id || fm.email || fm.phone));
	const isFamilyMember = Boolean(record.invited_by);

	if (!invitedSomeone && !isFamilyMember) return null;

	return (
		<div style={{ textAlign: 'center', padding: '8px 0' }}>
			{invitedSomeone && (
				<Typography variant="body1" sx={{ color: '#1565c0', fontWeight: 500 }}>
					{`הזמין את: `}
					{fm.member_id ? (
						<ReferenceField source="family_member.member_id" reference="member" link="show">
							<TextField source="email" />
						</ReferenceField>
					) : (
						<span>{fm.email || fm.phone}</span>
					)}
				</Typography>
			)}
			{isFamilyMember && (
				<Typography variant="body1" sx={{ color: '#6a1b9a', fontWeight: 500 }}>
					{`בן/בת משפחה של: `}
					<ReferenceField source="invited_by" reference="member" link="show">
						<TextField source="email" />
					</ReferenceField>
				</Typography>
			)}
		</div>
	);
};

const SendMemberMessages = () => {
	const [message, setMessage] = useState('');
	const [from, setFrom] = useState('GroupHug');
	const [title, setTitle] = useState('');
	const [isSendNotification, setIsSendNotification] = useState(false);
	const record = useRecordContext();
	const handleSendMessageToUser = async () => {
		try {
			await axios.post(`${constants.API_URL}member/${record.id}/message`, {
				text: message,
				notification: isSendNotification,
				from,
				title,
			});
			setMessage('');
		} catch (error) {}
	};
	return (
		<div style={{ width: 400, display: 'flex', flexDirection: 'column' }}>
			<Input placeholder="כותרת" value={title} onChange={(e) => setTitle(e.target.value)} />
			<Input
				style={{ marginBlock: 10 }}
				multiline
				placeholder="הודעה זאת תופיע למשתמש בכניסה לאפליקיה במסך inbox"
				value={message}
				onChange={(e) => setMessage(e.target.value)}
			/>
			<Typography textAlign={'right'}>{`מאת`}</Typography>
			<Input placeholder="מאת" value={from} onChange={(e) => setFrom(e.target.value)} />
			<Button
				style={{ display: 'flex', flexDirection: 'column', alignSelf: 'center', marginTop: 20 }}
				name="Send User Message"
				onClick={handleSendMessageToUser}
			>
				<Typography>{`Send Message To`}</Typography>
				<Typography>{`${record?.first_name}`}</Typography>
			</Button>
			<div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
				<Checkbox
					value={isSendNotification}
					onChange={(e) => setIsSendNotification(e.target.checked)}
					title="כולל נוטיפיקציה"
				/>
				<Typography>כולל נוטיפיקציה</Typography>
			</div>
		</div>
	);
};
const MemberShow = (props: any) => {
	const { record } = useShowController();
	const [meetings, setMeetings] = useState<any[]>([]);
	const [privateMeetings, setPrivateMeetings] = useState<PrivateMeeting | null>(null);


	useEffect(() => {
		const fetchMemberMeetings = async () => {
			try {
				const response = await axios.get(`${constants.API_URL}member/${record.id}/meetings`);
				setMeetings(response.data);
			} catch (error) {}
		};
		fetchMemberMeetings();
	}, [record]);


	useEffect(() => {

		const fetchMemberPrivateMeetings = async () => {
			try {
				const response = await axios.post(`${constants.API_URL}member/get-user-p-meetings`, {
					member_id: record.id,
				}, {
					headers: {
						'Authorization': `Bearer ${localStorage.getItem('token')}`
					}
				});
				setPrivateMeetings(response.data);

				
			} catch (error) {}
		};
		fetchMemberPrivateMeetings();
	}, [record]);



	const { permissions } = usePermissions();
	const logoutUser = async () => {
		try {
			await axios.get(`${constants.API_URL}user/${record.id}/logout`);
		} catch (error) {}
	};
	const isSuperAdmin = permissions === 'superadmin';
	const isAnchor = record && String(record.id) === FAMILY_INVITE_ANCHOR_ID;
	const notify = useNotify();
	return (
		<Show
			actions={
				<Stack py={1} direction={'row'} display={'flex'} justifyContent={'flex-end'}>
					<ShowButton />
					{true && (
						<Tooltip title="SuperAdmin בלבד">
							<span>
								<EditButton />
								{!isAnchor && <DeleteButton mutationMode="pessimistic" />}
							</span>
						</Tooltip>
					)}
				</Stack>
			}
		>
			<TabbedShowLayout>
				<Tab label="Info" hidden={!isSuperAdmin}>
					<AnchorMemberNotice />
					<TextField source="email" fullWidth />
					 
					<div style={{ textAlign: 'center' ,padding: '10px',justifyContent: 'center',display: 'flex'}}>
						<ImageField source="imageUri" />
					</div>
					<TextField source="first_name" fullWidth />
					<TextField source="last_name" fullWidth />
					<TextField source="phone" fullWidth />
					<TextField source="push_token" fullWidth />
					<ReferenceField source="company" reference="company">
						<TextField source="english_name" />
					</ReferenceField>
					<TextField source="platform" label="מערכת" textAlign="right" />
					<TextField source="version" label="גרסה מותקנת" textAlign="right" />
					<FamilyRelations />
					<Button
						className="flex flex-row gap-2 items-center"
						color="primary"
						onClick={async () => {
							await axios.post(`${constants.API_URL}member/forgot_password`, { email: record.email });
							notify('קישור לאיפוס סיסמא נשלח למייל של המשתמש');
						}}
					>
						<MdOutlineEmail />
						<span>שליחת קישור לאיפוס סיסמא</span>
					</Button>
					{permissions === 'superadmin' && (
						<Button color="error" style={{ alignSelf: 'center' }} onClick={logoutUser}>
							התנתקות מכל המכשירים
						</Button>
					)}
				</Tab>
				<Tab label="Meetings" hidden={!isSuperAdmin}>
					<List>
						{meetings?.map((x) => (
							<ListItem disablePadding>
								<ListItemButton style={{ padding: '0 10px' }} href={`#/meeting/${x._id}/show`}>
									<ListItemText
										style={{ textAlign: 'start' }}
										primary={x.title}
										secondary={
											<>
												<Typography sx={{ display: 'inline' }} variant="body1">
													{moment(x.time).format('DD/MM/YY HH:mm')}
												</Typography>
											</>
										}
									/>
								</ListItemButton>
							</ListItem>
						))}
					</List>

					{(() => {
						const sessions = (privateMeetings?.sessions || []).filter((s: any) => s?.service);
						const safeSpace = sessions.filter((s: any) => s.type === 'SAFE_SPACE');
						const others = sessions.filter((s: any) => s.type !== 'SAFE_SPACE');

						// Pair number per (host, cycle) grouped chronologically by createdAt
						const pairNumberMap = new Map<string, number>();
						const sorted = [...safeSpace].sort((a: any, b: any) =>
							moment(a.createdAt).diff(b.createdAt),
						);
						const counters: Record<string, number> = {};
						for (const s of sorted as any[]) {
							const hostId = s.service?.host?._id;
							if (!hostId) continue;
							const cycleKey = s.cycle_start_date
								? new Date(s.cycle_start_date).toISOString()
								: 'no-cycle';
							const key = `${hostId}:${cycleKey}`;
							counters[key] = (counters[key] || 0) + 1;
							pairNumberMap.set(s._id, counters[key]);
						}

						const renderSession = (x: any, opts?: { showPair?: boolean }) => (
							<ListItem disablePadding key={x._id}>
								<ListItemButton
									style={{ padding: '0 10px' }}
									href={`#/meeting/${x._id}/show`}
								>
									<ListItemText
										style={{
											textAlign: 'start',
											border: '1px solid #e0e0e0',
											borderRadius: '4px',
											padding: '12px',
											margin: '8px 0',
										}}
										primary={
											<div>
												<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
													<span>{x.title}</span>
													{opts?.showPair && pairNumberMap.get(x._id) != null && (
														<span
															style={{
																fontSize: '0.7rem',
																fontWeight: 'bold',
																padding: '2px 8px',
																borderRadius: '999px',
																background: x.subsidized ? '#e3f2fd' : '#fff3e0',
																color: x.subsidized ? '#1565c0' : '#e65100',
															}}
															title={x.subsidized ? 'טיפול מסובסד' : 'מחיר מלא'}
														>
															{`טיפול ${pairNumberMap.get(x._id)}/7`}
														</span>
													)}
													{x.type === '1TO1_SERIES' && (
														<span
															style={{
																fontSize: '0.7rem',
																fontWeight: 'bold',
																padding: '2px 8px',
																borderRadius: '999px',
																background: '#EEF4FF',
																color: '#1d3a8a',
															}}
															title="פגישה במסגרת חבילת סדרה"
														>
															{x.series_index && x.series_total
																? `סדרה ${x.series_index}/${x.series_total}`
																: 'סדרה'}
														</span>
													)}
													{/* GH-873: no "מודל חדש" badge any more (every subsidized session is on that model since 20/05/2026); the cycle caption stays */}
													{opts?.showPair && x.cycle_start_date && (
														<span style={{ fontSize: '0.65rem', color: '#888' }}>
															{`מחזור ${moment(x.cycle_start_date).format('DD/MM/YY')}`}
														</span>
													)}
												</div>
												<div
													style={{
														fontSize: '0.65rem',
														color: '#999',
														fontFamily: 'monospace',
														userSelect: 'all',
													}}
												>
													{`ID: ${x._id}`}
												</div>
											</div>
										}
										secondary={
											<>
												<Typography
													variant="body1"
													sx={{
														borderBottom: '1px solid #eee',
														paddingBottom: '4px',
														marginBottom: '4px',
													}}
												>
													{x?.service?.title?.he}
												</Typography>
												<Typography
													variant="body1"
													sx={{ color: '#2196f3', fontWeight: 500 }}
												>
													מחיר מנוי: {x?.price} ₪
												</Typography>
												{x.coupon && (() => {
													const servicePrice =
														x.service_price_at_booking ?? x?.service?.price ?? 0;
													const discount = Math.max(servicePrice - (x?.price ?? 0), 0);
													return (
														<Typography
															variant="body1"
															sx={{
																color: '#e65100',
																fontWeight: 500,
																fontSize: '0.85rem',
															}}
														>
															🎟️ קופון: -{discount} ₪ (מחיר מלא: {servicePrice} ₪)
														</Typography>
													);
												})()}
												{x.type === 'SAFE_SPACE' && (() => {
													const host = x?.service?.host;
													// COUPON_REDUCES_EXPERT_PAYOUT: when true AND this session
													// used a coupon, payout uses customer-paid price (so
													// expert-created coupons come out of the expert's pocket).
													// Company subsidies (no coupon) still pay the full service price.
													const COUPON_REDUCES_EXPERT_PAYOUT = true;
													const hasCoupon = !!x.coupon;
													const useCustomerPaid = COUPON_REDUCES_EXPERT_PAYOUT && hasCoupon;
													const basePrice = useCustomerPaid
														? x.price ?? x.service_price_at_booking ?? x?.service?.price ?? 0
														: x.service_price_at_booking ?? x?.service?.price ?? 0;
													const payout = x.subsidized
														? host?.safe_space_payment ||
															(host?.is_psychologist ? 350 : 250)
														: Math.floor(basePrice * 0.97);
													return (
														<Typography
															variant="body1"
															sx={{ color: '#2e7d32', fontWeight: 500 }}
														>
															תשלום למטפל: {payout} ₪
														</Typography>
													);
												})()}
												<Typography
													variant="body1"
													sx={{ color: '#666', fontSize: '0.9rem' }}
												>
													{moment(x.time).format('DD/MM/YY HH:mm')}
												</Typography>
												<Typography
													variant="body1"
													sx={{ color: '#666', fontSize: '0.9rem' }}
												>
													{x?.service?.host?.email}
												</Typography>
												<Typography
													variant="body1"
													sx={{ color: '#666', fontSize: '0.9rem' }}
												>
													{x?.service?.host?.name}
												</Typography>
											</>
										}
									/>
								</ListItemButton>
							</ListItem>
						);

						return (
							<>
								<Typography
									variant="h6"
									style={{ marginBottom: '16px', textAlign: 'start' }}
								>
									{`פגישות מרחב בטוח (${safeSpace.length})`}
								</Typography>
								<List>{safeSpace.map((x) => renderSession(x, { showPair: true }))}</List>

								<Typography
									variant="h6"
									style={{ marginBottom: '16px', marginTop: '24px', textAlign: 'start' }}
								>
									{`פגישות פרטיות אחרות (${others.length})`}
								</Typography>
								<List>{others.map((x) => renderSession(x))}</List>
							</>
						);
					})()}
				</Tab>
				<Tab label="Messages" hidden={!isSuperAdmin}>
					<SendMemberMessages />
				</Tab>
				{/* GH-826 — devices + Android step-recording check (server: admin / superadmin / editor) */}
				{record?._id && ['admin', 'superadmin', 'editor'].includes(String(permissions)) ? (
					<Tab label="מכשירים">
						<MemberDevicesTab memberId={String(record._id)} />
					</Tab>
				) : null}

				{record?._id ? (
					<Tab label="תכנית פרואקטיבית">
						<MemberProactivePlanForm member_id={record?._id} />
					</Tab>
				) : null}

					<Tab label="AI">
						<div>
							<Typography variant="h6" style={{ marginBottom: '16px', textAlign: 'start' }}>
								האווטר של המשתמש
							</Typography>
							<Typography>
							{record?.aiData ? (
								<pre style={{ whiteSpace: 'pre-wrap', textAlign: 'start' }}>
									{record.aiData}
								</pre>
							) : (
								'אין מידע AI'
							)}
							</Typography>
							{record?.aiRecommendation && (
								<div style={{ marginTop: '20px', textAlign: 'start' }}>
									<Typography variant="h6" style={{ marginBottom: '16px', textAlign: 'start' }}>
										ההמלצות של AI
									</Typography>
									{record.aiRecommendation.experts && (
										<div style={{ marginBottom: '20px' }}>
											<Typography variant="h6">מומחים מומלצים למשתמש</Typography>
											<div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: '8px' }}>
												{record.aiRecommendation.experts.items.map((expert: Expert) => (
													<div key={expert.id} style={{ width: '300px', border: '1px solid #ddd', padding: '12px', borderRadius: '8px' }}>
														<img src={expert?.data?.image}  style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: '4px' }} />
														<Typography variant="subtitle1" style={{ marginTop: '8px', fontWeight: 'bold' }}>{expert?.data?.name}</Typography>
														<Typography variant="subtitle1" style={{ marginTop: '8px', fontWeight: 'bold' }}>{expert?.data?.title}</Typography>
														<Typography variant="body2" color="textSecondary">{expert?.data?.description_lng?.he}</Typography>
														<Typography variant="body2" style={{ marginTop: '8px' }}>סיבת המלצה: {expert.reason}</Typography>
													</div>
												))}
											</div>
										</div>
									)}

									{record.aiRecommendation.meetings && (
										<div style={{ marginBottom: '20px' }}>
											<Typography variant="h6">{record.aiRecommendation.meetings.title}</Typography>
											<Typography variant="body2" style={{ marginBottom: '8px' }}>הרצאות מומלצות למשתמש</Typography>
											<div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
												{record.aiRecommendation.meetings.items.map((lecture: Lecture) => (
													<div key={lecture.id} style={{ padding: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
														<Typography variant="subtitle1">{lecture?.data?.service?.title}</Typography>
														<Typography variant="subtitle1">{lecture?.data?.service?.sub_title?.he}</Typography>
														<Typography variant="subtitle1">סיבה:{lecture.reason}</Typography>
														<img src={lecture?.data?.service?.image} alt={lecture.title} style={{ width: 200, height: '200px', objectFit: 'cover', borderRadius: '4px' }} />
														<Typography variant="body2" color="textSecondary"> {moment(lecture?.time).format('DD/MM/YY HH:mm')}</Typography>
													</div>
												))}
											</div>
										</div>
									)}



									{record?.aiRecommendation?.mediaItems && (
										<div style={{ marginBottom: '20px' }}>
											<Typography variant="h6">{record.aiRecommendation.mediaItems.title}</Typography>
											<Typography variant="body2" style={{ marginBottom: '8px' }}>VOD מומלצות למשתמש</Typography>
											<div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
												{record.aiRecommendation.mediaItems.items.map((MediaItem: MediaItem) => (
													<div key={MediaItem.id} style={{ padding: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
														<Typography variant="subtitle1">{MediaItem?.data?.title}</Typography>
														<Typography variant="subtitle1">{MediaItem?.data?.sub_title}</Typography>
														<Typography variant="subtitle1">סיבה:{MediaItem.reason}</Typography>
														<img src={MediaItem?.data?.thumbnail} alt={MediaItem.title} style={{ width: 200, height: '200px', objectFit: 'cover', borderRadius: '4px' }} />
													
													</div>
												))}
											</div>
										</div>
									)}

									{record.aiRecommendation.proactive && (
										<div>
											<Typography variant="h6">{record.aiRecommendation.proactive.title}</Typography>
											<Typography variant="body2" style={{ marginBottom: '8px' }}>בוסטים מומלצים למשתמש</Typography>
											<div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
												{record.aiRecommendation.proactive.items.map((service: Service) => (
													<div key={service.id} style={{ width: '250px', border: '1px solid #ddd', padding: '12px', borderRadius: '8px' }}>
														<img src={service?.data?.image} alt={service.title} style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '4px' }} />
														<Typography variant="subtitle1" style={{ marginTop: '8px' ,fontWeight: 'bold'}}>{service?.data?.title?.he}</Typography>
														<Typography variant="subtitle1" style={{ marginTop: '8px' ,fontWeight: 'bold',fontSize: '16px'}}>{service?.data?.sub_title?.he}</Typography>
														<Typography variant="subtitle1" style={{ marginTop: '8px',fontSize: '12px'}}>{service?.data?.description?.he}</Typography>
														<Typography variant="subtitle1" style={{ marginTop: '8px' ,fontSize: '12px'}}>סיבת המלצה: {service.reason}</Typography>
													</div>
												))}
											</div>
										</div>
									)}
								</div>
							)}
						</div>
					</Tab>
			</TabbedShowLayout>
		</Show>
	);
};
export default MemberShow;
